/**
 * Every route path in one place. Components import from here instead of writing
 * string literals, so a path change is one edit and a typo is a type error.
 */
export const paths = {
  home: '/',
  order: {
    /** The basket: every file in the order, and how each one prints. */
    root: '/order',
    /** Sign in, then say where it goes. */
    details: '/order/details',
    pay: '/order/pay',
  },
  orderStatus: (orderId = ':orderId') => `/orders/${orderId}`,
  /**
   * Where the header's cart icon goes, and where every retired URL lands.
   *
   * It is `paths.order.root`, not a page of its own. The basket USED to be a
   * separate `/cart`, because `/order/*` was a linear flow of prerequisites
   * and a cart is the opposite — no ids, reachable from anywhere. Now that
   * the basket IS the first step, that distinction has gone: one screen,
   * one URL, and `/cart` and `/basket` both redirect to it so older links
   * and anyone's bookmarks still work.
   */
  cart: '/order',
  /** Where a returning customer signs in. */
  signIn: '/signin',
  /** Everything this customer has bought. */
  orders: '/orders',
  /** Profile, saved addresses, sign out. Reached from the header icon. */
  account: '/account',
  /**
   * The published policy documents.
   *
   * Flat, top-level URLs rather than a `/legal/*` prefix: these get pasted into
   * a payment gateway's onboarding form, a marketplace listing and the odd
   * email, and `printoman.in/privacy` survives that better than a nested path.
   * The keys are the document slugs in features/legal/documents.ts, so
   * `paths.legal[slug]` is a total function over LegalSlug and the router can be
   * generated from the document list.
   */
  legal: {
    terms: '/terms',
    privacy: '/privacy',
    shipping: '/shipping-policy',
    refunds: '/refund-policy',
    returns: '/return-policy',
  },
} as const;

/**
 * Search-param names that carry checkout progress.
 *
 * These are part of the app's contract with the browser's back button and the
 * refresh key — see the persistence note in routes/order/steps.ts. Short names
 * because they end up in a URL a customer may see or share.
 */
export const searchParamKeys = {
  /** Upload id returned by POST /api/v1/uploads. */
  upload: 'u',
  /** Quote id returned by POST /api/v1/quotes. */
  quote: 'q',
  /** Order id returned by checkout. */
  order: 'o',
  /** Saved-address id chosen on the details step, carried into checkout. */
  address: 'a',
} as const;
